import { OperationStatus } from '../../../models/OperationStatus.interface';
import { ReceivingItemRef } from '../../../models/logs/ReceivingItemRef';
import { ReceivingLogDetails } from '../../../models/logs/ReceivingLogDetails';
import {
  UpdateReceivingLogConflict,
  UpdateReceivingLogOutcome,
  UpdateReceivingLogResponse,
} from '../../../models/logs/UpdateReceivingLogResponse';
import { itemCaption, normalizeItemType } from './container-items.rules';

/**
 * log-edit-outcome.rules.ts
 *
 * Pure interpretation of what `PUT /V1/receiving-log/{logId}` answered —
 * handoff-18 §4, §6 and §7. Session 2 built the request; everything here reads
 * the OUTCOME. Nothing in this file talks to the network, prints, or toasts.
 *
 * The thing to keep in view: a defect here does not produce a bad record. It
 * decides which physical labels a warehouse worker replaces on boxes that are
 * already sealed and shelved. Two rules carry that weight and each has its own
 * function below rather than being an inline expression at a call site:
 *
 * - {@link renumberedLabelWarning} exists because §6.2.3 says a non-empty
 *   `renumberedItems` means labels ALREADY IN THE WAREHOUSE are now wrong, and
 *   that must be said explicitly rather than buried in a toast. It returns text
 *   for the page to render, and deliberately nothing a toast helper would take.
 *   The edit screen does NOT print — reprinting is a separate, manual step
 *   through the log table's reprint modal — but removing the button does not
 *   remove the obligation to name the labels that went wrong, and this response
 *   is the ONLY place those facts exist (a refetch cannot reconstruct them).
 * - {@link isNotAttempted} is the §6 defence that a `statusCode: "0"` placeholder
 *   carries `isSuccess: false` and means "NOT ATTEMPTED", not "failed".
 *   `poCompletionStatus` is `"0"` unless a `completesPo` was sent. Surfacing that
 *   as an error would tell the user their save half-failed on every save that
 *   did not touch PO completion.
 *
 * And one thing this file must never produce: any claim that the PO note was
 * updated (§7). The GP note is written once, by the submit; no edit path writes
 * it, and the response no longer carries a note status to misread.
 */

// #region Per-part statuses

/**
 * Whether a `*Status` is a NOT-ATTEMPTED placeholder rather than a failure (§6).
 *
 * The backend builds these with `RecMgrOperationStatus.NotProcessed(...)`, which
 * sets `statusCode: "0"` and leaves `isSuccess: false` — the same convention the
 * submit response uses. `statusCode` is the discriminator, NOT `isSuccess` and not
 * the message text: the existing `ToastService.handleOperationStatus` guard also
 * requires the message to contain "not processed", which happens to be true today
 * but is a coincidence of wording, not the contract.
 *
 * Only `poCompletionStatus` still arrives as a `"0"` placeholder — it is the one
 * part of the request that is genuinely optional.
 */
export function isNotAttempted(status: OperationStatus | null | undefined): boolean {
  return status?.statusCode === '0';
}

/** One `*Status` off the response, paired with the label a message about it should carry. */
export interface LabeledPartStatus {
  label: string;
  status: OperationStatus;
}

/**
 * The per-part statuses worth surfacing: the ones that were ATTEMPTED and FAILED.
 *
 * This is the list to hand to the existing toast plumbing
 * (`ToastService.handleOperationStatus`, which renders a non-success status as
 * "{label} failed: {message}"). Successes are excluded so a save doesn't fire
 * three toasts, and `statusCode: "0"` placeholders are excluded because they did
 * not fail — see {@link isNotAttempted}.
 *
 * There is no PO-note part to scan: the response carries no note status, because
 * an edit never touches the GP note (§7). Adding one back would be the first step
 * toward telling a user their edit moved a vendor-visible note, which is false.
 */
export function partStatusFailures(response: UpdateReceivingLogResponse): LabeledPartStatus[] {
  const parts: LabeledPartStatus[] = [
    { label: 'Row changes', status: response?.rowsStatus },
    { label: 'Log row', status: response?.logRowStatus },
    { label: 'PO completion', status: response?.poCompletionStatus },
  ].filter((part): part is LabeledPartStatus => !!part.status);

  return parts.filter((part) => !part.status.isSuccess && !isNotAttempted(part.status));
}

// #endregion

// #region The interpreted 200

/**
 * What the screen shows after a `200`.
 *
 * Everything on here is derived from the response and nothing from the pending
 * edit: the point of §6.1/§6.2.1 is that the local preview was a guess and the
 * response is what is STORED.
 */
export interface LogEditSavedView {
  /**
   * The verdict to drive the UI off (§6). Never `"Failed"` — a refusal is a `400`
   * and a failure a `500`, so a `200` always means the transaction committed.
   */
  outcome: UpdateReceivingLogOutcome;

  /** True for `"Complete"`: the rows landed and nothing after the commit fell over. */
  savedCleanly: boolean;

  /**
   * What actually went wrong, verbatim from `incompleteParts` — the response's own
   * list, already labeled. NOT derived from the `*Status` fields, whose
   * `isSuccess: false` placeholders mean "not attempted".
   */
  warnings: string[];

  /** What is STORED on the log now (§6.2.1). Replaces the local quantity preview. */
  quantityDisplay: string;

  /** The note text that was persisted (the stored value when the request sent none). */
  storedNote: string;

  /** Plain-language lines describing what the save did. Cosmetic; never an error path. */
  summaryLines: string[];

  /** True when this edit changed no rows and no log-row field — a legitimate `200` (§4.4). */
  nothingChanged: boolean;

  /**
   * The response's own reprint gate. This screen does not print — it is the gate
   * on TELLING the user labels need replacing, which they then do from the log
   * table's reprint modal.
   */
  requiresReprint: boolean;

  /** Items whose caption CHANGED. Non-empty = labels in the warehouse are wrong. */
  renumberedItems: ReceivingItemRef[];

  /** Newly created items, never printed. Their ids come from the response (§3.6). */
  addedItems: ReceivingItemRef[];

  /** Captions of the renumbered labels, as the sticker reads them ("BOX 1 of 3"). */
  renumberedCaptions: string[];

  /**
   * True when `quantityDisplay` does not describe `receivingItems` (§6.1). Should
   * never happen on an edit that touched item rows — see
   * {@link quantityInvariantHolds} for what a `false` here actually means.
   */
  quantityDisagreesWithItems: boolean;
}

/**
 * Reads a `200` body into the view the screen renders (§6).
 *
 * Drives everything off `outcome` and `incompleteParts`, never off a `*Status`
 * `isSuccess` flag. Two documented messages are successes and must not read as
 * problems: `rowsStatus.message: "No row changes requested"` (§4.4 — a `PUT` that
 * changes nothing) and `logRowStatus.message: "No log-row fields to update"` (§6 —
 * an items-only or receipts-only edit legitimately touches no column).
 */
export function interpretSaveResponse(response: UpdateReceivingLogResponse): LogEditSavedView {
  const renumberedItems = response?.renumberedItems ?? [];
  const addedItems = response?.addedItems ?? [];

  return {
    outcome: response?.outcome ?? 'Complete',
    savedCleanly: (response?.outcome ?? 'Complete') === 'Complete',
    warnings: [...(response?.incompleteParts ?? [])],
    quantityDisplay: response?.quantityDisplay ?? '',
    storedNote: response?.items ?? '',
    summaryLines: saveSummaryLines(response),
    nothingChanged: changedNothing(response),
    requiresReprint: !!response?.requiresReprint,
    renumberedItems,
    addedItems,
    renumberedCaptions: renumberedItems.map(captionFor),
    quantityDisagreesWithItems: !quantityInvariantHolds(response),
  };
}

/**
 * The explicit "labels already in the warehouse are now wrong" statement (§6.2.3).
 *
 * Returns text for the PAGE to render. `renumberedItems` non-empty means stickers
 * that are already stuck to boxes now disagree with the log, so §6.2.3 says say so
 * explicitly and do not bury it in a toast — a toast is three seconds of yellow on
 * a screen the user is about to walk away from, and the boxes stay wrong.
 *
 * @param response The `200` body.
 * @param labelsWerePrinted `printHistory.lastPrintedDate != null` on the GET —
 *   only *successful* prints are recorded, so `false` means nothing is out in the
 *   world to be wrong and the sentence would be a scare with no referent.
 * @returns The statement, or null when no caption moved.
 */
export function renumberedLabelWarning(
  response: UpdateReceivingLogResponse,
  labelsWerePrinted: boolean,
): string | null {
  const items = response?.renumberedItems ?? [];
  if (items.length === 0) { return null; }

  const count = items.length;
  const noun = count === 1 ? 'label' : 'labels';
  // Neither sentence offers to print. This screen has no print path; reprinting is
  // a deliberate, separate step through the log table's reprint action, taken when
  // the user is at the printer with blank stock.
  if (!labelsWerePrinted) {
    return `${count} ${noun} on this log were renumbered by this edit. Nothing has been printed for ` +
      `this log yet, so there is nothing in the warehouse to replace — print them from the log table ` +
      `when you are ready.`;
  }
  return `${count} ${noun} for this log have already been printed and are now WRONG. Reprint them ` +
    `from the log table and replace the stickers on the boxes — the numbering on them no longer ` +
    `matches the log.`;
}

/**
 * The one-line "here is what changed" for the success toast.
 *
 * The edit screen closes itself on a clean save, so this sentence is the ONLY
 * report the user gets — it has to name what moved, not just that something did.
 * "Saved." alone would leave them reopening the log to check.
 *
 * Per mode, because the two editors changed different things and each has exactly
 * one fact worth carrying out of the modal: the container editor's is the derived
 * quantity string, and the items editor's is the recomposed note.
 *
 * Capped, because a toast is a strip across the screen and an items note can run to
 * several clauses. The cap is on the VALUE, never on the sentence around it, so a
 * truncated toast still reads as a sentence and still says what kind of thing moved.
 *
 * @param view The interpreted 200.
 * @param mode Which editor saved.
 * @returns The toast text.
 */
export function saveToastMessage(view: LogEditSavedView, mode: 'quantity' | 'items'): string {
  // §4.4: a PUT that changes nothing is a legitimate 200. Saying "quantity is now X"
  // there would claim an edit that did not happen.
  if (view.nothingChanged) { return 'Saved — nothing needed changing.'; }

  if (mode === 'quantity') {
    const quantity = (view.quantityDisplay ?? '').trim();
    return quantity ? `Saved — quantity is now ${quantity}.` : 'Saved.';
  }

  const note = (view.storedNote ?? '').trim();
  if (!note) { return 'Saved — the items note is now empty.'; }
  return `Saved — items now read: ${truncateForToast(note)}`;
}

/** The longest note fragment a toast carries before it stops being readable at a glance. */
const TOAST_VALUE_LIMIT = 120;

/** Trims on a WORD boundary so a truncated clause does not end mid-token. */
function truncateForToast(value: string): string {
  if (value.length <= TOAST_VALUE_LIMIT) { return value; }
  const cut = value.slice(0, TOAST_VALUE_LIMIT);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

/**
 * Whether the screen may close itself on this save, or has something the user must
 * read first.
 *
 * The edit screen auto-closes on a clean save — the toast is the report, and staying
 * open to show a result nobody needs is a second dismissal to perform. But three
 * outcomes are NOT dismissible in passing, and each is a reason to stay put:
 *
 *  - **a renumber warning.** §6.2.3 is explicit that a non-empty `renumberedItems`
 *    is rendered as a BLOCK, never a toast: it means stickers already stuck to boxes
 *    in the warehouse are now wrong, and the response is the only place the list of
 *    which ones exists. Three seconds of yellow on a screen the user is walking away
 *    from is precisely the failure mode that rule was written against.
 *  - **`incompleteParts`.** The rows committed but something after them did not, and
 *    the list of what is on screen only.
 *  - **a non-Complete outcome**, for the same reason.
 *
 * Note what is NOT consulted: whether anything CHANGED. A no-op save (§4.4) is clean
 * and closes like any other — there is nothing to read.
 */
export function canCloseAfterSave(view: LogEditSavedView, hasRenumberWarning: boolean): boolean {
  return view.savedCleanly && view.warnings.length === 0 && !hasRenumberWarning;
}

/**
 * The before/after quantity statement (§6.1/§6.2.1).
 *
 * `quantityDisplay` off the response is what is STORED, and the local preview was
 * only ever a guess. The case this exists for is the legacy log in §6.1: an edit
 * that adds containers to a log that had none DERIVES the quantity and OVERWRITES
 * the only record of what arrived ("3 boxes" → "1 PALLET"), so the user has to see
 * both halves rather than watch the field change under them.
 *
 * @returns The statement, or null when the stored value did not move.
 */
export function quantityChangeNotice(before: string | null | undefined, after: string | null | undefined): string | null {
  const from = (before ?? '').trim();
  const to = (after ?? '').trim();
  if (from === to) { return null; }
  return `Quantity was "${from || '(blank)'}" and is now "${to || '(blank)'}".`;
}

/**
 * §6.1's invariant: `quantityDisplay` is the collapsed, upper-cased summary of the
 * ACTIVE rows in `receivingItems`. Void a box and you get `"2 BOX"` next to two box
 * entries; add a pallet and `"3 BOX, 1 PALLET"` next to four rows. They cannot
 * disagree, because the string is derived from those rows inside the same
 * transaction that wrote them.
 *
 * Compared as a MULTISET of (count, type) pairs rather than as strings, so a
 * different ordering or spacing is not a false alarm — only a real disagreement
 * about how many of what.
 *
 * A `false` here does not mean the save failed (it committed — it is a `200`). It
 * means the two halves of the response describe different logs, and the only safe
 * reading is "re-check the labels before printing". It is also `false` in the
 * legitimate case §6.1 lists — an edit that touched no item rows returns the
 * unchanged stored string, which on a log whose free text was never derived (see
 * `quantityOutOfSync` on the GET) will not parse to the item rows. Callers must
 * therefore only act on this when the edit ACTUALLY touched item rows.
 */
export function quantityInvariantHolds(response: UpdateReceivingLogResponse): boolean {
  const expected = collapseItemCounts(response?.receivingItems ?? []);
  const actual = parseQuantityDisplay(response?.quantityDisplay ?? '');

  if (expected.size !== actual.size) { return false; }
  for (const [type, count] of expected) {
    if (actual.get(type) !== count) { return false; }
  }
  return true;
}

/** Whether this edit touched item rows at all — the gate on {@link quantityInvariantHolds}. */
export function touchedItemRows(response: UpdateReceivingLogResponse): boolean {
  return (response?.voidedCount ?? 0) > 0 || (response?.addedItems ?? []).length > 0;
}

// #endregion

// #region Refusals — §4, §5.2

/**
 * Which refusal this is. Deliberately does NOT distinguish §5.3's two routes to a
 * `409` (a stale screen, and losing a lock race to another in-flight edit): both
 * answer the same `{ message, detail }` shape, only the `message` differs, and the
 * required handling is identical either way.
 */
export type LogEditRefusalKind =
  | 'badRequest'   // 400 — nothing was written; the body is a PLAIN STRING
  | 'notFound'     // 404 — no such log, including one deleted or archived under the user
  | 'conflict'     // 409 — a concurrent edit stopped this one; reload and REDO
  | 'serverError'  // 500 — the transaction rolled back; nothing was written
  | 'network'      // never reached the server
  | 'unknown';

/** A refused save, read into the pieces the screen needs. */
export interface LogEditRefusal {
  kind: LogEditRefusalKind;

  /**
   * What to show the user. For a `400`/`404`/`500` this is the API's own body,
   * VERBATIM — a `400` names the offending ids and says what to do instead (§4.1),
   * and a `404` says the log was deleted or archived and the edit is unrecoverable.
   * For a `409` it is the wrapper's `message`, which names which ids appeared or
   * disappeared (§5.2).
   */
  message: string;

  /**
   * A `409`'s fresh state, byte-identical to `GET …/details` — feed it straight
   * back into the screen. Null on every other kind.
   */
  conflictDetail: ReceivingLogDetails | null;

  /**
   * True when the user's edit is gone and has to be REDONE from the re-rendered
   * state. Only a `409` — and it is never a licence to replay: the request is not
   * idempotent (it mints item positions and inserts receipt rows), so a replay that
   * half-ran the first time doubles them (§5.2).
   */
  mustRedoEdit: boolean;

  /** True when nothing was written, so the log is exactly as it was. */
  nothingWasWritten: boolean;
}

/** Fallback copy per kind, used only when the API sent no readable body. */
const REFUSAL_FALLBACKS: Record<LogEditRefusalKind, string> = {
  badRequest: 'The edit was refused and nothing was saved.',
  // §4.3's PO-less refusals are NOT offered a "set the PO first" remedy anywhere:
  // a log's PO cannot be changed after submit, by this endpoint or any other (§3.5).
  notFound: 'This log no longer exists — it was deleted or archived. Nothing about this edit was saved.',
  conflict: 'This log changed since you loaded it, so the edit was not applied. Reload and redo it.',
  serverError: 'The edit failed and was rolled back — nothing was saved. Try again.',
  network: 'Could not reach the server, so nothing was saved. Check your connection and try again.',
  unknown: 'The edit was not saved.',
};

/**
 * Reads a refused save into {@link LogEditRefusal} (§4, §5.2).
 *
 * The input is the normalized `{ message, details, originalError }` every
 * `ReceivingLogApiService` call rejects with. Two shapes have to be told apart and
 * getting it wrong is the whole point of this function:
 *
 * - A `400`/`404`/`500` body is a **PLAIN STRING**, not JSON. It is the message —
 *   it names the offending ids and says what to do instead — so it is shown as-is
 *   rather than replaced with copy of our own.
 * - A `409` body is **NOT a bare details payload**. It is a `{ message, detail }`
 *   wrapper. Treating it as the details payload loses the reason, and feeding the
 *   wrapper into the screen renders nothing.
 */
export function classifySaveRefusal(error: any): LogEditRefusal {
  const status: number | undefined = error?.originalError?.status ?? error?.details?.status;
  const body: unknown = error?.originalError?.error ?? error?.details?.body;
  const kind = refusalKindFor(status);

  if (kind === 'conflict') {
    const conflict = body as UpdateReceivingLogConflict | null;
    return {
      kind,
      message: readBodyMessage(body) ?? REFUSAL_FALLBACKS.conflict,
      conflictDetail: conflict?.detail ?? null,
      mustRedoEdit: true,
      nothingWasWritten: true,
    };
  }

  return {
    kind,
    message: readBodyMessage(body) ?? REFUSAL_FALLBACKS[kind],
    conflictDetail: null,
    mustRedoEdit: false,
    // Every documented refusal on this endpoint leaves the log untouched: a 400 is
    // pre-transaction, a 404 means the row was gone before anything was written,
    // and a 500 rolled back. 'unknown' is the only case we cannot promise that for.
    nothingWasWritten: kind !== 'unknown',
  };
}

/**
 * The message out of a refusal from the OLD endpoint, `PUT /V1/update-receiving-log`
 * (§7) — the one a confirmed legacy log (`hasStructuredRows: false`) still has to
 * use, because editing a legacy log's quantity TEXT is the one thing the new
 * endpoint cannot do.
 *
 * Its refusal body is a `RecMgrOperationStatus` (`{ isSuccess, message, userNote,
 * statusCode }`), NOT a bare string, and its `userNote` is where the actionable
 * half lives (it points at `PUT /V1/receiving-log/{logId}`). Reading that body as
 * text renders "[object Object]".
 *
 * That endpoint no longer touches the GP note on any save — it writes
 * `MM.ReceivingLog` and nothing else — so `items` is safe to send on every save
 * through it. Its `ReceivingNoteStatus` response field survives for wire
 * compatibility and is always null; nothing here reads it.
 *
 * @param body The error body — `err.originalError.error` off the normalized shape.
 * @returns The message to show, or null when the body carries nothing readable, so
 *   the caller keeps its own generic copy rather than replacing it with worse.
 */
export function legacyEndpointRefusalMessage(body: unknown): string | null {
  if (typeof body === 'string') { return body.trim() || null; }
  const status = body as OperationStatus | null;
  const parts = [status?.message, status?.userNote].filter((part) => !!(part ?? '').trim());
  return parts.length > 0 ? parts.join(' ') : null;
}

// #endregion

// #region Helpers

function refusalKindFor(status: number | undefined): LogEditRefusalKind {
  switch (status) {
    case 400: return 'badRequest';
    case 404: return 'notFound';
    case 409: return 'conflict';
    case 500: return 'serverError';
    case 0: return 'network';
    default: return status != null && status >= 500 ? 'serverError' : 'unknown';
  }
}

/**
 * The human message out of an error body, whichever of the two shapes it is.
 * Returns null (rather than a placeholder) when there is nothing readable, so the
 * caller substitutes its own per-kind copy instead of showing "undefined".
 */
function readBodyMessage(body: unknown): string | null {
  if (typeof body === 'string') { return body.trim() || null; }
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

/** An item's caption as the printed sticker reads it — "BOX 2 of 3", or "BOX" when it is the only one. */
function captionFor(item: ReceivingItemRef): string {
  return itemCaption({
    itemType: item.itemType,
    typeIndex: item.typeIndex,
    typeTotal: item.typeTotal,
  });
}

/** How many active items of each (normalized) type, from `receivingItems`. */
function collapseItemCounts(items: ReceivingItemRef[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const item of items ?? []) {
    const type = normalizeItemType(item?.itemType);
    if (!type) { continue; }
    counts.set(type, (counts.get(type) ?? 0) + 1);
  }
  return counts;
}

/**
 * Parses a stored quantity string ("3 BOX, 2 PALLET") back into (count, type)
 * pairs. Returns an empty map for free text it cannot read ("3 boxes and a
 * pallet"), which is why {@link quantityInvariantHolds} must only be trusted on an
 * edit that touched item rows.
 */
function parseQuantityDisplay(display: string): Map<string, number> {
  const counts = new Map<string, number>();
  for (const part of (display ?? '').split(',')) {
    const match = part.trim().match(/^(\d+)\s+(.+)$/);
    if (!match) { continue; }
    const type = normalizeItemType(match[2]);
    if (!type) { continue; }
    counts.set(type, (counts.get(type) ?? 0) + Number(match[1]));
  }
  return counts;
}

/**
 * Whether the save changed nothing at all — §4.4's legitimate `200`.
 *
 * The row counts answer for the rows. The log row has no count, so the only signal
 * is `logRowStatus`'s documented message; the doc quotes it verbatim, and a
 * mismatch here only costs a cosmetic summary line, never a wrong error.
 */
function changedNothing(response: UpdateReceivingLogResponse): boolean {
  const rowsUntouched =
    (response?.voidedCount ?? 0) === 0 &&
    (response?.addedItems ?? []).length === 0 &&
    (response?.addedReceiptIds ?? []).length === 0 &&
    (response?.updatedReceiptCount ?? 0) === 0 &&
    (response?.deletedReceiptCount ?? 0) === 0;

  return rowsUntouched && !logRowWasWritten(response);
}

/** True when a column on `MM.ReceivingLog` was actually written. */
function logRowWasWritten(response: UpdateReceivingLogResponse): boolean {
  const message = (response?.logRowStatus?.message ?? '').toLowerCase();
  // "No log-row fields to update" is a SUCCESS, not a problem (§6) — it just means
  // no column moved. It also appears when the only logRow field sent was `id` or
  // `quantity`, both of which are ignored (§3.5).
  return !message.includes('no log-row fields');
}

/** Plain-language lines describing what the save did. Cosmetic only. */
function saveSummaryLines(response: UpdateReceivingLogResponse): string[] {
  const lines: string[] = [];
  const voided = response?.voidedCount ?? 0;
  const added = (response?.addedItems ?? []).length;
  const addedReceipts = (response?.addedReceiptIds ?? []).length;
  const updatedReceipts = response?.updatedReceiptCount ?? 0;
  const deletedReceipts = response?.deletedReceiptCount ?? 0;

  if (voided > 0) { lines.push(`${voided} item ${voided === 1 ? 'row' : 'rows'} voided.`); }
  if (added > 0) { lines.push(`${added} item ${added === 1 ? 'row' : 'rows'} added.`); }
  if (addedReceipts > 0) { lines.push(`${addedReceipts} receipt ${addedReceipts === 1 ? 'row' : 'rows'} added.`); }
  if (updatedReceipts > 0) { lines.push(`${updatedReceipts} receipt ${updatedReceipts === 1 ? 'row' : 'rows'} updated.`); }
  if (deletedReceipts > 0) { lines.push(`${deletedReceipts} receipt ${deletedReceipts === 1 ? 'row' : 'rows'} deleted.`); }

  // Only ever a success statement (§6). Note that nothing here mentions the PO
  // note, and nothing added here ever may: no edit path writes it, so any line
  // claiming otherwise would be false (§7). A spec pins that property.
  if (response?.poCompletionStatus && !isNotAttempted(response.poCompletionStatus) && response.poCompletionStatus.isSuccess) {
    lines.push(response.poCompletionStatus.message);
  }

  return lines;
}

// #endregion
